import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Grid,
  Button,
  Stack
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import InPageNavSidebar, { InPageSection } from '../components/common/InPageNavSidebar';

const TermsOfService = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // Ensure the page starts at the top when navigated to
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const sections: InPageSection[] = [
    { id: 'agreement', label: 'Agreement to Terms' },
    { id: 'service', label: 'Description of Service' },
    { id: 'accounts', label: 'User Accounts' },
    { id: 'acceptable-use', label: 'Acceptable Use' },
    { id: 'privacy', label: 'Data & Privacy' },
    { id: 'payments', label: 'Payment Terms' },
    { id: 'ip', label: 'Intellectual Property' },
    { id: 'availability', label: 'Service Availability' },
    { id: 'disclaimer', label: 'Disclaimers & Liability' },
    { id: 'indemnification', label: 'Indemnification' },
    { id: 'termination', label: 'Termination' },
    { id: 'law', label: 'Governing Law' },
    { id: 'changes', label: 'Changes to Terms' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>      
      {/* Fixed Sidebar */}
      <InPageNavSidebar sections={sections} offsetTop={96} />
      
      <Box sx={{ pt: 12, pb: 8, bgcolor: 'background.default' }}>
        <Container 
          maxWidth="lg" 
          sx={{ 
            ml: { xs: 0, lg: '320px' },
            px: { xs: 2, sm: 3, md: 4 },
            maxWidth: { xs: '100%', lg: 'calc(100% - 320px)' },
            width: { xs: '100%', lg: 'calc(100% - 320px)' }
          }}
        >
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    gutterBottom
                    sx={{
                      textAlign: 'left',
                      mb: 4,
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      backgroundImage: 'var(--primary-gradient)',
                      backgroundClip: 'text',
                      color: 'transparent',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Terms of Service
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 6 }}
                  >
                    Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </Typography>
                </motion.div>

                <Paper
                  elevation={2}
                  sx={{
                    p: { xs: 3, md: 6 },
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <motion.div variants={itemVariants} id="agreement">
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                      Agreement to Terms
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                      These Terms of Service ("Terms") govern your use of the AkMo Books accounting software and related 
                      services (the "Service") provided by AkMo Solutions Private Limited ("Company," "we," "our," or "us"). 
                      By accessing or using our Service, you agree to be bound by these Terms.
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                      If you do not agree to these Terms, please do not use our Service. We reserve the right to modify 
                      these Terms at any time, and your continued use of the Service constitutes acceptance of any changes.
                    </Typography>
                  </motion.div>

                  <motion.div variants={itemVariants} id="service">
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                      Description of Service
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      AkMo Books is a cloud-based accounting software designed for Indian small and medium businesses. 
                      Our Service includes but is not limited to:
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Invoice Management"
                          secondary="Create, customize, and send professional invoices"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="GST Compliance"
                          secondary="GST-ready features for Indian tax regulations"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Financial Reporting"
                          secondary="Generate financial reports and business insights"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Expense Tracking"
                          secondary="Track and categorize business expenses"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Client Management"
                          secondary="Manage customer information and transaction history"
                        />
                      </ListItem>
                    </List>
                  </motion.div>

                  <motion.div variants={itemVariants} id="accounts">
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }}>
                      User Accounts and Registration
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      To use our Service, you must create an account by providing accurate and complete information. 
                      You are responsible for:
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Account Security"
                          secondary="Maintaining the confidentiality of your account credentials"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Accurate Information"
                          secondary="Providing accurate, current, and complete information"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Account Activity"
                          secondary="All activities that occur under your account"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Notification"
                          secondary="Notifying us immediately of any unauthorized use of your account"
                        />
                      </ListItem>
                    </List>
                  </motion.div>

                  <motion.div variants={itemVariants} id="acceptable-use">
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }}>
                      Acceptable Use Policy
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      You agree to use our Service only for lawful purposes and in accordance with these Terms. 
                      You agree NOT to:
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Illegal Activities"
                          secondary="Use the Service for any unlawful purpose or in violation of any applicable laws"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Unauthorized Access"
                          secondary="Attempt to gain unauthorized access to any part of the Service or other users' accounts"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Malicious Software"
                          secondary="Upload, transmit, or distribute any malicious code, viruses, or harmful software"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Spam or Abuse"
                          secondary="Send unsolicited communications or engage in any form of spam or abuse"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Reverse Engineering"
                          secondary="Attempt to reverse engineer, decompile, or disassemble the Service"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Data Mining"
                          secondary="Use automated systems to extract data from the Service without permission"
                        />
                      </ListItem>
                    </List>
                  </motion.div>

                  <motion.div variants={itemVariants} id="privacy">
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }}>
                      Data and Privacy
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      Your privacy is important to us. Our collection and use of personal information is governed by 
                      our Privacy Policy, which is incorporated into these Terms by reference. By using our Service, 
                      you consent to the collection and use of information as described in our Privacy Policy.
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      You retain ownership of all data you input into our Service. We will not access, use, or share 
                      your data except as necessary to provide the Service or as required by law.
                    </Typography>
                  </motion.div>

                  <motion.div variants={itemVariants} id="payments">
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }}>
                      Payment Terms
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      Our Service may include both free and paid features. For paid features:
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Billing"
                          secondary="Fees are charged in advance on a monthly or annual basis"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Payment Methods"
                          secondary="We accept various payment methods including credit cards and bank transfers"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Price Changes"
                          secondary="We may change our prices with 30 days' notice to existing customers"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Refunds"
                          secondary="Refunds are handled on a case-by-case basis in accordance with our refund policy"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Taxes"
                          secondary="You are responsible for any applicable taxes based on your location"
                        />
                      </ListItem>
                    </List>
                  </motion.div>

                  <motion.div variants={itemVariants} id="ip">
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }}>
                      Intellectual Property Rights
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      The Service and its original content, features, and functionality are owned by AkMo Solutions 
                      Private Limited and are protected by international copyright, trademark, patent, trade secret, 
                      and other intellectual property laws.
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      You may not:
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Copy or Modify"
                          secondary="Copy, modify, or create derivative works of the Service"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Distribute"
                          secondary="Distribute, sell, or sublicense the Service"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Remove Notices"
                          secondary="Remove or alter any proprietary notices or labels"
                        />
                      </ListItem>
                    </List>
                  </motion.div>

                  <motion.div variants={itemVariants} id="availability">
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }}>
                      Service Availability
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      We strive to provide continuous service availability, but we cannot guarantee that the Service 
                      will be available at all times. The Service may be temporarily unavailable due to:
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Maintenance"
                          secondary="Scheduled maintenance and updates"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Technical Issues"
                          secondary="Technical difficulties or system failures"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Force Majeure"
                          secondary="Events beyond our control such as natural disasters or government actions"
                        />
                      </ListItem>
                    </List>
                  </motion.div>

                  <motion.div variants={itemVariants} id="disclaimer">
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }}>
                      Disclaimers and Limitations of Liability
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
                      EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR 
                      A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                      SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, 
                      DATA, OR USE, ARISING OUT OF OR RELATING TO YOUR USE OF THE SERVICE.
                    </Typography>
                  </motion.div>

                  <motion.div variants={itemVariants} id="indemnification">
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }}>
                      Indemnification
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      You agree to indemnify, defend, and hold harmless AkMo Solutions Private Limited and its 
                      officers, directors, employees, and agents from and against any claims, damages, obligations, 
                      losses, liabilities, costs, or debt, and expenses (including attorney's fees) arising from:
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Your Use"
                          secondary="Your use of the Service"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Violation of Terms"
                          secondary="Your violation of these Terms"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Content"
                          secondary="Your content or data"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Third Party Rights"
                          secondary="Your violation of any third party's rights"
                        />
                      </ListItem>
                    </List>
                  </motion.div>

                  <motion.div variants={itemVariants} id="termination">
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }}>
                      Termination
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      We may terminate or suspend your account and access to the Service immediately, without prior 
                      notice or liability, for any reason whatsoever, including without limitation if you breach 
                      these Terms.
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      Upon termination, your right to use the Service will cease immediately. We may delete your 
                      account and data after a reasonable period, subject to our data retention policies.
                    </Typography>
                  </motion.div>

                  <motion.div variants={itemVariants} id="law">
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }}>
                      Governing Law and Dispute Resolution
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      These Terms shall be governed by and construed in accordance with the laws of India, without 
                      regard to its conflict of law provisions.
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      Any disputes arising out of or relating to these Terms or the Service shall be resolved through 
                      binding arbitration in accordance with the Arbitration and Conciliation Act, 2015, or through 
                      the courts of competent jurisdiction in [Your City], India.
                    </Typography>
                  </motion.div>

                  <motion.div variants={itemVariants} id="changes">
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }}>
                      Changes to Terms
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      We reserve the right to modify or replace these Terms at any time. If a revision is material, 
                      we will try to provide at least 30 days' notice prior to any new terms taking effect.
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      Your continued use of the Service after any such changes constitutes your acceptance of the new Terms.
                    </Typography>
                  </motion.div>

                  <motion.div variants={itemVariants} id="contact">
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, mt: 4 }}>
                      Contact Information
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                      If you have any questions about these Terms of Service, please contact us:
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body1" paragraph>
                        <strong>Email:</strong> legal@akmobooks.com
                      </Typography>
                      <Typography variant="body1" paragraph>
                        <strong>Phone:</strong> +91-6363154142
                      </Typography>
                      <Typography variant="body1" paragraph>
                        <strong>Address:</strong><br />
                        AkMo Solutions Private Limited<br />
                        No.14, SP, Sidco T.V.K Industrial Estate<br />
                        Guindy, Chennai, Tamilnadu, 600032<br />
                        India
                      </Typography>
                    </Box>
                  </motion.div>

                  <Divider sx={{ my: 4 }} />
                  
                  <motion.div variants={itemVariants}>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      These Terms of Service are effective as of {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} 
                      and will remain in effect except with respect to any changes in its provisions in the future.
                    </Typography>
                  </motion.div>
                </Paper>
              </motion.div>
        </Container>
      </Box>

      {/* Footer */}    </Box>
  );
};

export default TermsOfService;
